import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { 
  initDB, 
  cacheUsuario, 
  getUsuarioCache, 
  sincronizarDadosPendentes, 
  getStatusOffline 
} from './offlineStorage';

const UnifiedAuthContext = createContext(null);

const JORNYX_API_URL = process.env.REACT_APP_BACKEND_URL;

export const UnifiedAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [jornyxToken, setJornyxToken] = useState(null);
  const [jornyxUser, setJornyxUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasJornyxAccess, setHasJornyxAccess] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [offlineStatus, setOfflineStatus] = useState({ total_pendentes: 0 });
  const [isSyncing, setIsSyncing] = useState(false);

  // Monitorar status de conexão
  useEffect(() => {
    const handleOnline = () => {
      console.log('[UnifiedAuth] Conexão restaurada');
      setIsOnline(true);
      // Tenta sincronizar ao voltar online
      if (jornyxToken) {
        syncPendingData();
      }
    };

    const handleOffline = () => {
      console.log('[UnifiedAuth] Conexão perdida');
      setIsOnline(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [jornyxToken]);

  // Inicializar banco offline e verificar sessão
  useEffect(() => {
    const initialize = async () => {
      await initDB();
      
      // Verificar se há sessão salva
      const savedSession = localStorage.getItem('unified_session');
      if (savedSession) {
        try {
          const session = JSON.parse(savedSession);
          setUser(session.user);
          setJornyxToken(session.jornyxToken);
          setJornyxUser(session.jornyxUser);
          setHasJornyxAccess(!!session.jornyxToken);
          
          // Atualizar status offline
          const status = await getStatusOffline(session.jornyxUser?.empregado_id);
          setOfflineStatus(status);
        } catch (e) {
          localStorage.removeItem('unified_session');
        }
      }
      setLoading(false);
    };

    initialize();
  }, []);

  // Atualizar status de pendências periodicamente
  useEffect(() => {
    const interval = setInterval(async () => {
      if (jornyxUser?.empregado_id) {
        const status = await getStatusOffline(jornyxUser.empregado_id);
        setOfflineStatus(status);
      }
    }, 10000);

    return () => clearInterval(interval);
  }, [jornyxUser]);

  const saveSession = (userData, jornyxTokenData, jornyxUserData) => {
    const session = {
      user: userData,
      jornyxToken: jornyxTokenData,
      jornyxUser: jornyxUserData
    };
    localStorage.setItem('unified_session', JSON.stringify(session));
  };

  const syncPendingData = useCallback(async () => {
    if (!jornyxToken || isSyncing) return;
    
    setIsSyncing(true);
    try {
      const result = await sincronizarDadosPendentes(JORNYX_API_URL, jornyxToken);
      if (result.success) {
        const status = await getStatusOffline(jornyxUser?.empregado_id);
        setOfflineStatus(status);
      }
    } catch (error) {
      console.error('[UnifiedAuth] Erro na sincronização:', error);
    } finally {
      setIsSyncing(false);
    }
  }, [jornyxToken, jornyxUser, isSyncing]);

  const login = async (identifier, password) => {
    // Login direto no Jornyx por CPF
    const cpfLimpo = identifier.replace(/[.\-\s]/g, '');
    
    // Se estiver online, tenta login normal
    if (navigator.onLine) {
      try {
        const response = await fetch(`${JORNYX_API_URL}/api/app/login-by-cpf`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            cpf: cpfLimpo,
            senha: password
          })
        });
        
        const data = await response.json();
        
        if (!response.ok) {
          throw new Error(data.detail || 'CPF ou senha inválidos');
        }
        
        // Login bem sucedido
        const userData = data.user;
        const tokenValue = data.access_token;
        
        // Cachear usuário para login offline futuro
        await cacheUsuario({
          ...userData,
          cpf: cpfLimpo,
          senha_hash: btoa(password) // Simples encoding para cache local
        });
        
        setUser(userData);
        setJornyxToken(tokenValue);
        setJornyxUser(userData);
        setHasJornyxAccess(true);
        saveSession(userData, tokenValue, userData);
        
        return { success: true, user: userData, hasJornyxAccess: true, offline: false };
      } catch (error) {
        // Se falhou e pode ser erro de rede, tenta offline
        if (!navigator.onLine || error.message.includes('fetch')) {
          return tryOfflineLogin(cpfLimpo, password);
        }
        throw error;
      }
    } else {
      // Modo offline - tenta login com cache
      return tryOfflineLogin(cpfLimpo, password);
    }
  };

  const tryOfflineLogin = async (cpf, password) => {
    try {
      const cachedUser = await getUsuarioCache(cpf);
      
      if (!cachedUser) {
        throw new Error('Sem conexão e usuário não encontrado no cache. Conecte-se à internet para o primeiro login.');
      }
      
      // Verificar senha (comparação simples com cache)
      if (cachedUser.senha_hash !== btoa(password)) {
        throw new Error('Senha incorreta');
      }
      
      console.log('[UnifiedAuth] Login offline bem-sucedido');
      
      // Usar dados do cache
      const userData = cachedUser;
      const offlineToken = `offline_${Date.now()}_${cachedUser.empregado_id}`;
      
      setUser(userData);
      setJornyxToken(offlineToken);
      setJornyxUser(userData);
      setHasJornyxAccess(true);
      saveSession(userData, offlineToken, userData);
      
      return { 
        success: true, 
        user: userData, 
        hasJornyxAccess: true, 
        offline: true,
        message: 'Login realizado em modo offline. Dados serão sincronizados quando a conexão for restaurada.'
      };
    } catch (error) {
      console.error('[UnifiedAuth] Erro no login offline:', error);
      throw error;
    }
  };

  const logout = () => {
    setUser(null);
    setJornyxToken(null);
    setJornyxUser(null);
    setHasJornyxAccess(false);
    localStorage.removeItem('unified_session');
  };

  const value = {
    user,
    jornyxToken,
    jornyxUser,
    loading,
    hasJornyxAccess,
    login,
    logout,
    isAuthenticated: !!user && !!jornyxToken,
    isOnline,
    offlineStatus,
    isSyncing,
    syncPendingData,
    apiUrl: JORNYX_API_URL
  };

  return (
    <UnifiedAuthContext.Provider value={value}>
      {children}
    </UnifiedAuthContext.Provider>
  );
};

export const useUnifiedAuth = () => {
  const context = useContext(UnifiedAuthContext);
  if (!context) {
    throw new Error('useUnifiedAuth deve ser usado dentro de UnifiedAuthProvider');
  }
  return context;
};
